import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { IconUserCircle } from "@tabler/icons-react";
import { NavLang } from "./layout";
import ProfilePicture from "../../ProfilePicture";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import Skeleton from "../../skeleton/Skeleton";
import SkeletonTextLine from "../../skeleton/SkeletonTextLine";

export default function Header({
  title,
  subtitle,
  actions,
  rightContent,
  onTitleClick,
}: {
  title: string;
  subtitle?: string;
  /** Extra actions inserted before NavLang + avatar. */
  actions?: ReactNode;
  /** Replaces the entire right side (NavLang + avatar) with custom content. */
  rightContent?: ReactNode;
  /** When set, the title/subtitle block becomes clickable (e.g. scroll-to-top). */
  onTitleClick: (() => void) | null;
}): React.JSX.Element {
  const { currentUser } = useCurrentUser();

  return (
    <div className="bg-white/90 backdrop-blur-md px-5 py-3 border-b border-secondary-soft-yellow-grey">
      {/* min-h-12 reserves the two-line (title + subtitle) height so headers
          without a subtitle (JuisciGPT, Rooms) match the Home header height. */}
      <div className="flex items-center justify-between min-h-12">
        <div
          className={
            onTitleClick === null
              ? "flex flex-col gap-0"
              : "flex flex-col gap-0 cursor-pointer"
          }
          onClick={onTitleClick === null ? undefined : onTitleClick}
        >
          <h1 className="text-2xl font-bold  tracking-tight text-primary-black">
            {title}
          </h1>
          {subtitle !== undefined && (
            <p className="text-xs font-medium text-secondary-bluegrey-4">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4">
          {rightContent === undefined ? (
            <>
              {actions}
              <NavLang />
              {currentUser !== null ? (
                <Link to="/profile">
                  <ProfilePicture user={currentUser} height={32} badge={null} />
                </Link>
              ) : (
                <Link to="/login">
                  <IconUserCircle size={26} className="text-primary-black" />
                </Link>
              )}
            </>
          ) : (
            rightContent
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Loading silhouette for {@link Header} as presented in ScrollableLayout's fixed
 * header slot: same px-5 py-3 + bottom border, `min-h-12` two-line height and
 * safe-area-top so the white fills the notch. `hasSubtitle` mirrors pages that
 * show a subtitle (Discovery) vs those that don't; `leadingActions` is the
 * number of action icons rendered before the always-present language switcher +
 * avatar (Discovery passes 1 for its search icon).
 */
export function HeaderSkeleton({
  hasSubtitle,
  leadingActions,
}: {
  hasSubtitle: boolean;
  leadingActions: number;
}): React.JSX.Element {
  return (
    <div
      className="border-b border-secondary-soft-yellow-grey bg-white/90 backdrop-blur-md"
      style={{ paddingTop: "var(--safe-area-top)" }}
    >
      <div className="flex min-h-12 items-center justify-between px-5 py-3">
        {/* No gap — the title/subtitle line boxes already include their leading,
            so the real Header stacks them flush (gap-0). Title = 2xl (24/32),
            subtitle = xs (12/16). */}
        <div className="flex flex-col">
          <SkeletonTextLine width={120} fontSize={24} lineHeight={32} />
          {hasSubtitle ? (
            <SkeletonTextLine width={150} fontSize={12} lineHeight={16} />
          ) : null}
        </div>
        {/* Right actions (gap-4): leading action icons, language switcher, then
            the 32px avatar (ProfilePicture height 32). */}
        <div className="flex items-center gap-4">
          {Array.from({ length: leadingActions }).map((_, idx) => (
            <Skeleton key={idx} $variant="circular" width={24} height={24} />
          ))}
          <Skeleton $variant="circular" width={24} height={24} />
          <Skeleton $variant="circular" width={32} height={32} />
        </div>
      </div>
    </div>
  );
}
